import { useAddressQuery } from '~/queries/useAddressQuery.ts';
import { TTaxResidencies } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/types.ts';
import { Grid, Typography } from '@mui/material';
import FormGridItem from '~/components/FormComponents/FormGridItem.tsx';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import { AddRemoveBlock } from '~/components/AddRemoveBlock/AddRemoveBlock.tsx';
import { useFormContext, useWatch } from 'react-hook-form';
import { EMPTY_TAX_RESIDENCY_JURISDICTIONS } from '~/modules/identification/forms/TaxResidencyAdditionalInformation/constants.ts';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import { FormRadioGroup } from '~/components/FormComponents/FormRadioGroup.tsx';
import { reasonsIdentificationNumber } from '~/modules/identification/forms/AdditionalInformation/constants.ts';

interface ITaxResidencyJurisdictionsProps {
  index: number;
  append: (item: TTaxResidencies) => void;
  remove: (index: number) => void;
  section?: string;
}

export const TaxResidencyJurisdictions = ({
  append,
  remove,
  index,
  section,
}: ITaxResidencyJurisdictionsProps) => {
  const { setValue, trigger } = useFormContext();
  const { countryOptions } = useAddressQuery();
  const basePath = section ? `${section}.taxResidencies.${index}` : `taxResidencies.${index}`;

  const tinUnavailableReason = useWatch({
    name: `${basePath}.tinUnavailableReason`,
  });

  const handleTinChange = () => {
    setValue(`${basePath}.tinUnavailableReason`, null, {
      shouldValidate: true,
      shouldDirty: false,
    });
    setValue(`${basePath}.tinUnavailableExplanation`, null, {
      shouldValidate: false,
      shouldDirty: false,
    });
  };

  const handleTinUnavailableReasonChange = (value: string) => {
    if (value === 'A') {
      setValue(`${basePath}.tinUnavailableExplanation`, null, {
        shouldValidate: false,
        shouldDirty: false,
      });
      trigger(`${basePath}.tinUnavailableReason`);
    }
    setValue(`${basePath}.tin`, null, {
      shouldValidate: true,
    });
  };

  return (
    <>
      <FormGridItem>
        <FormAutocomplete
          name={`${basePath}.jurisdictionCountry`}
          options={countryOptions}
          inputProps={{
            placeholder: 'Оберіть країну',
          }}
          label="Держава / юрисдикція резидентства для цілей оподаткування"
          fullWidth
        />
      </FormGridItem>
      <FormGridItem>
        <FormInput
          name={`${basePath}.tin`}
          label="ІПН або його функціональний еквівалент"
          placeholder="Введіть ІПН або його функціональний еквівалент"
          onChange={handleTinChange}
          fullWidth
        />
      </FormGridItem>
      <Typography variant="body1">
        Якщо Ви з будь-якої причини не вказуєте номер платника податків, будь ласка, зазначте
        причину
      </Typography>
      <Grid size={12}>
        <FormRadioGroup
          name={`${basePath}.tinUnavailableReason`}
          options={reasonsIdentificationNumber}
          onChange={handleTinUnavailableReasonChange}
        />
      </Grid>
      {['B', 'C'].includes(tinUnavailableReason) && (
        <FormInput
          name={`${basePath}.tinUnavailableExplanation`}
          label="Пояснення причини відсутності ІПН"
          placeholder="Введіть пояснення причини відсутності ІПН"
          fullWidth
        />
      )}
      <Grid size={12}>
        <AddRemoveBlock
          add={() => append(EMPTY_TAX_RESIDENCY_JURISDICTIONS)}
          remove={() => remove(index)}
          isPlusShown
          isMinusShown={!section && index === 0 ? false : true}
          plusText='Натисніть на "+" для додавання юрисдикції'
          minusText='Натисніть на "-" для видалення юрисдикції'
        />
      </Grid>
    </>
  );
};
